import { ServerError, ServerParseError, useApolloClient } from '@apollo/client';
import { AlertButton, useIonAlert, useIonToast } from '@ionic/react';
import { compare } from 'fast-json-patch';
import {
  ReactElement,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react';
import {
  GetScratchpadDocument,
  GetScratchpadQuery,
  ListScratchpadsDocument,
  ListScratchpadsQuery,
  Scratchpad,
  ScratchpadApp,
  UpdateScratchpadInput,
  useCreateScratchpadMutation,
  useGetScratchpadQuery,
  useUpdateScratchpadMutation,
} from '../../../api/graphql/generated';
import {
  useMachineConnected,
  useMachineOfflineToast,
} from '../../../api/hooks/machine.hooks';
import { useScratchpadUtils } from '../../../api/hooks/scratchpad.hooks';
import {
  trackScratchpadOptimisticLockAction,
  trackScratchpadSaveAttemptWhileInFlight,
} from '../../../config/analytics';
import i18n from '../../../i18n';
import { getLocalizedText } from '../../../i18n/i18n.utils';
import { useMachineContext } from '../../../pages/machine/machine-context-provider';
import {
  ScratchpadAppDataDiffFn,
  ScratchpadAppDataDiffFnSlugMappings,
} from '../../../scratchpad/app-slugs';
import { logger } from '../../../util/logger';
import { LoadingRetry } from '../../graphql/LoadingRetry';
import { SaveStatusToast } from '../../toast/toast.gql';
import { useScratchpadPlaylistContext } from '../playlist/scratchpad-playlist.hooks';

interface SaveScratchpadOptions {
  forceSave?: boolean;
  saveAsDelta?: boolean;
}

type SaveScratchpadFn = (
  scratchpad: Scratchpad,
  options?: SaveScratchpadOptions
) => Promise<Scratchpad | undefined>;

export interface ScratchpadServiceContextValue {
  scratchpad: Scratchpad;
  save: SaveScratchpadFn;
  remove: (scratchpadId: string) => Promise<Scratchpad | undefined>;
  restore: (scratchpadId: string) => Promise<Scratchpad | undefined>;
  isSaving: boolean;
}

export const ScratchpadServiceContext = createContext<
  ScratchpadServiceContextValue | undefined
>(undefined);

export interface ScratchpadServiceProviderProps {
  scratchpadId?: string;
  app?: ScratchpadApp;
  children: ReactElement | ((props: ChildProps) => ReactElement);
}

interface ChildProps {
  scratchpad: Scratchpad;
}

const noResultsComponent = (
  <h2>{i18n.t('common:scratchpad_no_scratchpad_found')}</h2>
);

export const ScratchpadServiceContextProvider = (
  props: ScratchpadServiceProviderProps
): ReactElement | null => {
  const { scratchpadId, children } = props;

  const { data, loading, refetch, error } = useGetScratchpadQuery({
    variables: { input: { id: scratchpadId as string } },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: scratchpadId === undefined,
  });

  const scratchpad: Scratchpad | undefined = data?.getScratchpad;

  const scratchpadSaveFnRef = useRef<SaveScratchpadFn>();
  const failedScratchpadUpdateRef = useRef<Scratchpad | undefined>();

  const client = useApolloClient();

  const [
    createScratchpadMutation,
    { error: createError, loading: isCreatingScratchpad },
  ] = useCreateScratchpadMutation();

  const [
    updateScratchpadMutation,
    { error: updateError, loading: isUpdatingScratchpad },
  ] = useUpdateScratchpadMutation();

  const isSaving = useMemo(() => {
    return isCreatingScratchpad || isUpdatingScratchpad;
  }, [isCreatingScratchpad, isUpdatingScratchpad]);

  const { remove, restore } = useScratchpadUtils();

  const [showInFlightError] = useIonToast();
  const { machine } = useMachineContext();
  const isConnected = useMachineConnected({ machine });
  const { showMachineOfflineToast } = useMachineOfflineToast();
  const { statuses } = useScratchpadPlaylistContext();

  const save: SaveScratchpadFn = async (
    incomingScratchpad,
    options
  ): Promise<Scratchpad | undefined> => {
    const { forceSave = false, saveAsDelta = false } = options ?? {};

    if (isSaving) {
      const errorMessage =
        getLocalizedText('common:scratchpad_saving_retry_prompt') ??
        'This part is still saving. Please try again shortly.';

      showInFlightError({
        message: errorMessage,
        duration: 3500,
        color: 'danger',
      });

      trackScratchpadSaveAttemptWhileInFlight();
      return;
    }

    const existingScratchpad = data?.getScratchpad;
    const scratchpadInput = getScratchpadInput({
      incomingScratchpad,
      existingScratchpad,
      options: {
        forceSave,
        saveAsDelta,
      },
    });

    // store scratchpad in ref. if we failed because of an optimistic
    // lock this ref will be used to resave
    failedScratchpadUpdateRef.current = incomingScratchpad;
    const isDelta = saveAsDelta && !forceSave;

    try {
      if (incomingScratchpad.id) {
        let appData: Record<string, unknown> | undefined = undefined;
        const savedScratchpad = await updateScratchpadMutation({
          variables: {
            input: scratchpadInput,
            options: {
              forceSave,
              isDelta,
            },
          },
          // the fetch policy is set to no-cache to merge deltas
          fetchPolicy: 'no-cache',
          onCompleted: (data) => {
            const { cache } = client;

            if (!isDelta) {
              // the cache needs to be updated with the data since the
              // fetch policy is no-cache
              cache.writeQuery<GetScratchpadQuery>({
                query: GetScratchpadDocument,
                variables: {
                  input: { id: scratchpadId },
                },
                data: {
                  getScratchpad: {
                    ...data.updateScratchpad,
                  },
                },
              });

              return;
            }

            // if the incoming appData is undefined we are updating the
            // scratchpad properties and not the appData so we will
            // use the existing appData
            appData =
              incomingScratchpad?.appData ?? existingScratchpad?.appData;

            cache.writeQuery<GetScratchpadQuery>({
              query: GetScratchpadDocument,
              variables: {
                input: { id: scratchpadId },
              },
              data: {
                getScratchpad: {
                  ...data.updateScratchpad,
                  appData: appData,
                },
              },
            });
          },
        });

        if (isConnected === false) {
          const isOnPlaylist = statuses?.find(
            (sp) => sp.scratchpadId === scratchpadId
          )
            ? true
            : false;
          if (isOnPlaylist) {
            showMachineOfflineToast();
          }
        }

        if (savedScratchpad.data?.updateScratchpad) {
          if (saveAsDelta) {
            return {
              ...savedScratchpad.data.updateScratchpad,
              appData,
            };
          }

          return savedScratchpad.data.updateScratchpad;
        }
      } else {
        const savedScratchpad = await createScratchpadMutation({
          variables: {
            input: getScratchpadInputProperties(incomingScratchpad),
          },
          update: (cache, { data: createScratchPadData }) => {
            const cacheData = cache.readQuery<ListScratchpadsQuery>({
              query: ListScratchpadsDocument,
              variables: {
                input: {
                  machineId: createScratchPadData?.createScratchpad.machineId,
                },
              },
            });
            const items: Partial<Scratchpad>[] = [
              ...(cacheData?.listScratchpads.items ?? []),
            ];
            if (createScratchPadData) {
              items.push(createScratchPadData?.createScratchpad);
            }
            cache.writeQuery({
              query: ListScratchpadsDocument,
              variables: {
                input: {
                  machineId: createScratchPadData?.createScratchpad.machineId,
                },
              },
              data: {
                listScratchpads: {
                  ...cacheData?.listScratchpads,
                  items,
                  total: items.length,
                },
              },
            });
          },
        });

        if (savedScratchpad.data?.createScratchpad) {
          return savedScratchpad.data.createScratchpad;
        }
      }
    } catch (error) {
      logger.error('failed to create/update scratchpad', { error });
    }
  };

  // assign save function to ref for use in error memo
  scratchpadSaveFnRef.current = save;

  const [showOptimisticLockAlert] = useIonAlert();

  const getNetworkError = (
    error: Error | ServerParseError | ServerError
  ): string | undefined => {
    /**
     * The statusCode isn't part of the type and will not be
     * included although it does exist in the returned object as
     * documented here: https://github.com/apollographql/apollo-link/issues/300.
     */
    if ('statusCode' in error) {
      const { statusCode } = error;

      // This status follows the standard https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/413
      if (statusCode === 413) {
        return i18n.t('common:scratchpad_too_large_error');
      }
    }
  };

  const transformedUpdateError = useMemo(() => {
    if (updateError) {
      const { graphQLErrors, networkError } = updateError;
      const gqlErrors = graphQLErrors.map(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (e) => e.extensions?.exception?.name
      );

      if (gqlErrors.includes('optimistic_lock')) {
        const buttons: AlertButton[] = [];

        if (failedScratchpadUpdateRef.current) {
          buttons.push({
            text: i18n.t('common:force_save'),
            handler: async () => {
              if (
                scratchpadSaveFnRef.current &&
                failedScratchpadUpdateRef.current
              ) {
                trackScratchpadOptimisticLockAction(true);
                await scratchpadSaveFnRef.current(
                  failedScratchpadUpdateRef.current,
                  {
                    forceSave: true,
                  }
                );
              }
            },
          });
        }

        buttons.push({
          text: i18n.t('common:discard_changes'),
          role: 'destructive',
          handler: async () => {
            trackScratchpadOptimisticLockAction(false);
            await refetch();
          },
        });

        showOptimisticLockAlert(
          i18n.t('common:scratchpad_optimistic_lock'),
          buttons
        );
      } else if (networkError) {
        return getNetworkError(networkError) ?? updateError;
      } else {
        return updateError;
      }
    }
  }, [updateError, refetch, showOptimisticLockAlert]);

  /**
   * Empty scratchpad is used for when we are creating a new
   * scratchpad
   */
  const emptyScratchpad = useMemo<Scratchpad>(() => {
    return {
      app: props.app,
      appId: props.app?.id,
      license: props.app?.applicationLicense,
      machineId: props.app?.machineId,
    } as Scratchpad;
  }, [props.app]);

  if (!scratchpadId) {
    return (
      <>
        <ScratchpadServiceContext.Provider
          value={{
            scratchpad: emptyScratchpad,
            save,
            remove,
            restore,
            isSaving,
          }}
        >
          {typeof children === 'function'
            ? children({ scratchpad: emptyScratchpad })
            : children}
        </ScratchpadServiceContext.Provider>
        <SaveStatusToast error={createError} />
      </>
    );
  } else {
    return (
      <LoadingRetry
        loading={loading}
        refetch={refetch}
        error={error}
        results={{
          hasResults: scratchpad ? true : false,
          noResultsComponent,
        }}
      >
        {scratchpad && (
          <ScratchpadServiceContext.Provider
            value={{
              scratchpad: scratchpad,
              save,
              remove,
              restore,
              isSaving,
            }}
          >
            {typeof children === 'function'
              ? children({ scratchpad: scratchpad })
              : children}
          </ScratchpadServiceContext.Provider>
        )}
        <SaveStatusToast error={transformedUpdateError} />
      </LoadingRetry>
    );
  }
};

export const useScratchpadServiceContext =
  (): ScratchpadServiceContextValue => {
    const context = useContext(ScratchpadServiceContext);
    return context as ScratchpadServiceContextValue;
  };

const getDiffFnByAppSlug = (appSlug?: string): ScratchpadAppDataDiffFn => {
  let diffFn: ScratchpadAppDataDiffFn | undefined;

  if (!diffFn && appSlug) {
    diffFn = ScratchpadAppDataDiffFnSlugMappings[appSlug];
  }

  if (!diffFn) {
    diffFn = ({ oldAppData, newAppData }) => {
      return compare(oldAppData, newAppData);
    };
  }

  return diffFn;
};

interface GetScratchpadInputParams {
  incomingScratchpad: Scratchpad;
  existingScratchpad?: Scratchpad;
  options?: {
    forceSave?: boolean;
    saveAsDelta?: boolean;
  };
}

const getScratchpadInput = (
  props: GetScratchpadInputParams
): UpdateScratchpadInput => {
  const { incomingScratchpad, existingScratchpad, options } = props;
  const { forceSave = false, saveAsDelta = false } = options ?? {};

  const diffFn = getDiffFnByAppSlug(incomingScratchpad.app.slug);

  let scratchpadInput: UpdateScratchpadInput =
    getScratchpadInputProperties(incomingScratchpad);

  if (saveAsDelta && existingScratchpad && !forceSave) {
    const appDataDiff = diffFn({
      oldAppData: existingScratchpad.appData,
      newAppData: incomingScratchpad.appData ?? {},
    });

    scratchpadInput = {
      ...scratchpadInput,
      appData: appDataDiff.length > 0 ? appDataDiff : undefined,
    };
  }

  return scratchpadInput;
};

const getScratchpadInputProperties = (
  scratchpad: Scratchpad
): UpdateScratchpadInput => {
  const {
    __typename,
    app: _app,
    license: _license,
    createdBy: _createdBy,
    updatedBy: _updatedBy,
    ...input
  } = scratchpad;

  return input;
};
